import { ProjectInstallationStatusEnum } from '@api/enum';
import { IProjectInstallation } from '@api/models';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import RequiredMark from '@components/FormFields/RequiredMark';
import useDialog from '@hooks/use-dialog';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { DeleteIcon, LockIcon, PlusIcon, UnlockIcon } from '@icon/index';
import { FormHelperText } from '@library/forms';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage, FieldArray, Form, Formik } from 'formik';
import { useBusinessDevelopmentListData } from 'modules/client-installation/hooks/useBusinessDevelopmentListData';
import useProjectInstallationUpdateForm from 'modules/project-installation/hooks/useProjectInstallationUpdateForm';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import { countryOptions } from 'modules/shared/helpers/shared.constants';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import DeleteProjectInstallationDrawer from '../DeleteProjectInstallationDrawer/DeleteProjectInstallationDrawer';
import FileUploadProjectInstallation from '../FileUploadProjectInstallation/FileUploadProjectInstallation';
import ProjectInstallationTimeline from '../ProjectInstallationTimeline/ProjectInstallationTimeline';

interface UpdateProjectInstallationFormProps {
    clientInstallationId: string;
    projectInstallationId: string;
    initialData: IProjectInstallation;
    setData: (data: any) => void;
}

const UpdateProjectInstallationForm = (props: UpdateProjectInstallationFormProps) => {
    const { clientInstallationId, projectInstallationId, initialData, setData } = props;
    const formHook = useProjectInstallationUpdateForm(clientInstallationId, projectInstallationId, initialData);
    const deleteProjectInstallationDailog = useDialog<IProjectInstallation>();
    const { businessDevelopmentUser, isLoading } = useBusinessDevelopmentListData();
    const queryClient = useQueryClient();
    const [enableEdit, setEnableEdit] = useState<boolean>(false);

    const BdUserOption = useMemo(() => {
        if (!businessDevelopmentUser) return [];
        return [
            { label: 'Select BD', value: '' },
            ...businessDevelopmentUser.map((val) => ({ label: val.name, value: val._id })),
        ];
    }, [businessDevelopmentUser]);

    const statusOptions = useMemo(
        () => [
            { label: 'Approved', value: ProjectInstallationStatusEnum.APPROVED },
            { label: 'Pending', value: ProjectInstallationStatusEnum.PENDING },
        ],
        []
    );

    const handleSubmit = async (values: any) => {
        try {
            const data = await formHook.submit(values);
            if (data) {
                await queryClient.invalidateQueries(['project-installation', clientInstallationId, data._id]);
            }
            setEnableEdit(false);
            toast.success('Project Updated Successfully');
        } catch (err) {
            console.error('Error submitting form:', err);
        }
    };

    return (
        <>
            <Formik
                initialValues={formHook.initialValues}
                enableReinitialize
                validationSchema={formHook.schema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue, resetForm }) => (
                    <Form>
                        {/* Basic Info */}
                        <Card>
                            <Card.Header className="flex justify-between items-center">
                                <div>Basic Information</div>
                                <div>
                                    {enableEdit ? (
                                        <div className="flex gap-4">
                                            <Button
                                                type="button"
                                                variant="shaded"
                                                onClick={() => {
                                                    resetForm();
                                                    setEnableEdit(false);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button disabled={formHook.isSubmitting || !enableEdit}>
                                                {formHook.isSubmitting ? 'Updating...' : 'Save'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button type="button" variant="shaded" onClick={() => setEnableEdit(true)}>
                                            <LockIcon width={18} height={22} className="text-grayscale-600 mr-1" />
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <fieldset disabled={!enableEdit}>
                                    <Label>
                                        Name <RequiredMark />
                                    </Label>
                                    <FormikInput name="name" type="text" />
                                    <HelpText variant="error">
                                        <ErrorMessage name="name" />
                                    </HelpText>

                                    <div className="mt-4">
                                        <Label>
                                            Status <RequiredMark />
                                        </Label>
                                        <FormikSelect name="status" options={statusOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="status" />
                                        </HelpText>
                                    </div>

                                    <div className="mt-4">
                                        <Label>
                                            Business Development <RequiredMark />
                                        </Label>
                                        <div className="flex gap-2">
                                            <FormikSelect name="businessDevelopmentUser" options={BdUserOption} />
                                            {isLoading && <CircularProgress size={14} />}
                                        </div>
                                        <HelpText variant={'error'}>
                                            <ErrorMessage name="businessDevelopmentUser" />
                                        </HelpText>
                                    </div>
                                </fieldset>
                            </Card.Body>
                        </Card>

                        <fieldset disabled={!enableEdit}>
                            {/* POCs */}
                            <Card className="mt-4">
                                <Card.Header>Project POCs</Card.Header>
                                <Card.Body>
                                    <FieldArray name="poc">
                                        {({ remove, push }) => (
                                            <>
                                                {values.poc.map((_, index) => (
                                                    <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4">
                                                        <div className="md:col-span-3">
                                                            <Label>
                                                                Name <RequiredMark />
                                                            </Label>
                                                            <FormikInput name={`poc.${index}.name`} type="text" />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.name`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <Label>
                                                                Email <RequiredMark />
                                                            </Label>
                                                            <FormikInput name={`poc.${index}.email`} type="email" />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.email`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-3">
                                                            <Label>Phone</Label>
                                                            <FormikInput
                                                                name={`poc.${index}.phoneNumber`}
                                                                type="number"
                                                            />
                                                            <HelpText variant={'error'}>
                                                                <ErrorMessage name={`poc.${index}.phoneNumber`} />
                                                            </HelpText>
                                                        </div>
                                                        <div className="md:col-span-2">
                                                            <Label>Designation</Label>
                                                            <FormikInput
                                                                name={`poc.${index}.designation`}
                                                                type="text"
                                                            />
                                                        </div>
                                                        <div className="md:col-span-1 flex items-center pt-6">
                                                            <Button type="button" onClick={() => remove(index)}>
                                                                <DeleteIcon />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                                <Button
                                                    className="mt-4"
                                                    size="sm"
                                                    type="button"
                                                    onClick={() =>
                                                        push({
                                                            name: '',
                                                            email: '',
                                                            phoneNumber: '',
                                                            designation: '',
                                                        })
                                                    }
                                                >
                                                    <PlusIcon />
                                                </Button>
                                            </>
                                        )}
                                    </FieldArray>
                                </Card.Body>
                            </Card>

                            {/* Location */}
                            <Card className="mt-4">
                                <Card.Header>Location</Card.Header>
                                <Card.Body>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>
                                                Country <RequiredMark />
                                            </Label>
                                            <FormikSelect name="location.country" options={countryOptions} showSearch />
                                            <HelpText variant="error">
                                                <ErrorMessage name="location.country" />
                                            </HelpText>
                                        </div>
                                        <div>
                                            <Label>
                                                City <RequiredMark />
                                            </Label>
                                            <FormikInput name="location.city" type="text" />
                                            <HelpText variant="error">
                                                <ErrorMessage name="location.city" />
                                            </HelpText>
                                        </div>

                                        <div>
                                            <Label>Latitude</Label>
                                            <FormikInput name="location.latitude" type="text" />
                                        </div>
                                        <div>
                                            <Label>Longitude</Label>
                                            <FormikInput name="location.longitude" type="text" />
                                        </div>

                                        <div className="md:col-span-2">
                                            <Label>Google Maps Link</Label>
                                            <FormikInput name="location.googleMapsLink" type="text" />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Contract */}
                            <Card className="mt-4">
                                <Card.Header>Contract</Card.Header>
                                <Card.Body>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <Label>Start Date</Label>
                                            <FormikDatePicker name="contract.startDate" />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="contract.startDate" />
                                            </FormHelperText>
                                        </div>

                                        <div>
                                            <Label>End Date</Label>
                                            <FormikDatePicker name="contract.endDate" />
                                            <FormHelperText variant="error">
                                                <ErrorMessage name="contract.endDate" />
                                            </FormHelperText>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </fieldset>

                        <Button
                            type="submit"
                            className="mt-6  w-full sm:w-auto"
                            disabled={formHook.isSubmitting || !enableEdit}
                        >
                            {formHook.isSubmitting ? 'Updating...' : 'Update'}
                        </Button>
                    </Form>
                )}
            </Formik>

            <FileUploadProjectInstallation
                clientInstallationId={clientInstallationId}
                initialData={initialData}
                setData={setData}
                enableEdit={enableEdit}
            />

            {enableEdit && (
                <Card className="mt-4">
                    <Card.Header>Project Delete</Card.Header>
                    <Card.Body>
                        <Button onClick={() => deleteProjectInstallationDailog.show(initialData)}>Delete</Button>
                    </Card.Body>
                </Card>
            )}

            <DeleteProjectInstallationDrawer
                open={deleteProjectInstallationDailog.isOpen}
                onClose={deleteProjectInstallationDailog.hide}
                clientInstallationId={clientInstallationId}
                projectInstallation={deleteProjectInstallationDailog.data}
            />

            <ProjectInstallationTimeline />
        </>
    );
};

export default UpdateProjectInstallationForm;
